import { useState, useEffect, useRef, useCallback } from "react";
import { useTheme, mono } from "./appHelpers";
import CompareCanvas from "./CompareCanvas";
import AttitudeCanvas from "./AttitudeCanvas";
import StandaloneCGCanvas from "./StandaloneCGCanvas";
import OrbitalCoverageCanvas from "./OrbitalCoverageCanvas";

// ── Helpers ───────────────────────────────────────────────────────────────────
function arrow(ctx, x1, y1, x2, y2, color, lw = 1.5) {
  if (Math.abs(x2 - x1) < 1 && Math.abs(y2 - y1) < 1) return;
  const a = Math.atan2(y2 - y1, x2 - x1), h = 7;
  ctx.strokeStyle = color; ctx.lineWidth = lw; ctx.fillStyle = color;
  ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - h * Math.cos(a - 0.4), y2 - h * Math.sin(a - 0.4));
  ctx.lineTo(x2 - h * Math.cos(a + 0.4), y2 - h * Math.sin(a + 0.4));
  ctx.closePath(); ctx.fill();
}
function rr(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y); ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r); ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h); ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r); ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}
const fmt = (v, d = 4) => v.toFixed(d);
const MFONT = "'JetBrains Mono', monospace";

// ── Phase metadata ────────────────────────────────────────────────────────────
// Physics convention:
//   Repulsion coil  = RIGHT wall of hull interior  (+x, xRel = +L_emms/2)
//   Damper          = LEFT  wall of hull interior  (−x, xRel = −L_emms/2)
//
// 6-phase cycle:
//   S0: Body coasts forward with momentum from S5 (or is still in cycle 1). Drag decelerates.
//   S1: EMMS charges — fe ramps up, mass → damper, hull → repulsion (+x)
//   S2: Full EMMS + hull cold gas — hull surges right
//   S3: Mass hits damper — inelastic stop; vCom→0 at transition
//   S4: Mass's OWN cold gas fires — only mass moves +x back to repulsion wall; hull frozen
//   S5: Mass arrives at repulsion wall — inelastic impact transfers mass momentum to whole body;
//       hull+mass drift forward (+x) together; this forward velocity carries into S0
const PH = [
  { name: "STABLE HOLD",       color: "#ef4444", desc: "S0: Hull holds position at new displaced location. Mass at repulsion wall. Drag accumulates as energy debt." },
  { name: "REPULSION STROKE",  color: "#fbbf24", desc: "S1: EM coil fires — mass launches toward damper. Hull recoils toward repulsion (+x). Action-reaction." },
  { name: "COASTING STROKE",   color: "#0e9cc0", desc: "S2: EMMS off. Mass coasts left to damper; hull coasts right — both moving in opposite directions by inertia." },
  { name: "DAMPING STROKE",    color: "#22c55e", desc: "S3: Mass reaches damper. Inelastic stop — all motion ceases. Hull holds its rightward shift." },
  { name: "COLD GAS STROKE",   color: "#a855f7", desc: "S4: Mass-only cold gas fires. Mass accelerates back toward repulsion wall. Hull frozen — no reaction." },
  { name: "MASS COASTING",     color: "#f59e0b", desc: "S5: Mass coasts alone to repulsion wall. On arrival, momentum transfers — hull steps forward to next cycle position." },
];
// Wall-time (seconds) per phase — controls visual pacing only
// S4 brief burn shown in slow-mo (0.8s wall for ~0.2s real); S5 long coast compressed (1.5s wall for ~32s real)
const PH_WALL = [1.2, 0.8, 0.9, 0.7, 0.8, 1.5];

// ── Default parameters ────────────────────────────────────────────────────────
const DEF = {
  m_hull:    7.0,    // kg
  m_emms:    1.0,    // kg
  L_hull:    0.30,   // m
  L_emms:    0.064,  // m  (±32 mm)
  F_absorb:  0.800,  // N  (repulsion peak force)
  F_reset:   0.010,  // N  (cold gas MIB force)
  J_mib:     0.002,  // Ns (Minimum Impulse Bit — burn stops after this; mass then coasts)
  F_thrust:  0.005,  // N  (cold gas thruster)
  dt_thrust: 0.0006, // s  (0.6 ms valve open)
  F_drag:      22e-6,  // N
  tau_coil:    0.004,  // s
  isp:         70,     // s
  t_coast_wall: 0.5,   // s  — S5 animation wall-time (500 ms default; physics runs faster via speed ratio)
};
const G0 = 9.81;
const T_ORB_CYCLE = 2 * Math.PI * Math.sqrt(Math.pow((6371 + 500) * 1000, 3) / 3.986e14);

// ── Physics step ──────────────────────────────────────────────────────────────
// ── Physics engine ────────────────────────────────────────────────────────────
// Two-body CoM/relative decomposition. 6 phases.
//
// Forces:
//   fe      = internal EMMS force on mass (fe<0 → mass→damper, hull→repulsion)
//   ft_hull = external thrust on hull (S1, S2)
//   ft_mass = external thrust on mass only (S4: mass has its OWN cold gas thruster)
//             No reaction on hull — propellant expelled outside the system.
//
// a_com = (ft_hull + ft_mass − F_drag) / M
// a_rel = fe·(1/m_emms + 1/m_hull) + ft_mass/m_emms + (F_drag − ft_hull)/m_hull
//
// Key identity (S4): a_hull = a_com − (m_emms/M)·a_rel = −F_drag/m_hull ≈ 0 ✓ (hull frozen)
//
// Wall constraints (inelastic at both walls):
//   xr > +half → repulsion wall hit: vRel ≤ 0   (S4: mass arrives → vCom holds combined velocity)
//   xr < −half → damper      hit: vRel ≥ 0   (S3: inelastic stop; vCom→0 at S4 transition)
function physStep(s, dt, p) {
  const M = p.m_hull + p.m_emms;
  // S0: hull + mass completely frozen. No forces, no motion.
  // Drag "accumulates" as orbital energy debt but is not shown as body movement.
  if (s.phase === 0) {
    return { ...s, vCom: 0, vRel: 0, vHull: 0, vMass: 0,
             aHull: 0, aMass: 0, aCom: 0, fe: 0, ft: 0, tAbs: s.tAbs + dt };
  }
  let fe = 0, ft_hull = 0, ft_mass = 0;
  switch (s.phase) {
    // S1: EMMS fires (repulsion stroke) — mass gets impulse toward damper, hull recoils +x
    case 1: fe = -p.F_absorb * (1 - Math.exp(-s.tReal / p.tau_coil)); ft_hull = p.F_thrust; break;
    // S2: coasting stroke — EMMS off, no thrust; mass coasts left, hull coasts right (opposite)
    case 2: break;
    // S3: damper absorbed all momentum — no forces. Mass held at damper wall.
    case 3: break;
    case 4: ft_mass = p.F_reset; break;
    case 5: break;  // body coasts at vCom from S4 end; drag decelerates
    default: break;
  }
  const a_com = (ft_hull + ft_mass - p.F_drag) / M;
  const a_rel = fe * (1 / p.m_emms + 1 / p.m_hull)
              + ft_mass / p.m_emms
              + (p.F_drag - ft_hull) / p.m_hull;
  let vc = s.vCom + a_com * dt;
  let xc = s.xCom + s.vCom * dt;
  let vr = s.vRel + a_rel * dt;
  let xr = s.xRel + s.vRel * dt;
  const half = p.L_emms / 2;
  if (xr > half)  { xr = half;  vr = Math.min(0, vr); }  // repulsion wall
  if (xr < -half) { xr = -half; vr = Math.max(0, vr); }  // damper wall
  return {
    ...s,
    xCom: xc, vCom: vc, xRel: xr, vRel: vr,
    vHull: vc - (p.m_emms / M) * vr,
    vMass: vc + (p.m_hull / M) * vr,
    aHull: (-fe + ft_hull - p.F_drag) / p.m_hull,
    aMass: (fe + ft_mass) / p.m_emms,
    aCom:  a_com,
    fe, ft: ft_hull + ft_mass,
    maxXRel: Math.max(s.maxXRel, Math.abs(xr)),
    tAbs: s.tAbs + dt,
  };
}

// ── Main component ────────────────────────────────────────────────────────────
export default function CyclePage() {
  const C = useTheme();
  const canvasRef = useRef(null);
  const sim = useRef(null);
  const raf = useRef(null);
  const lastTs = useRef(null);
  const paramsRef = useRef(DEF);
  const frameCount = useRef(0);

  const [p, setP] = useState(DEF);
  const [running, setRunning] = useState(false);
  const [tele, setTele] = useState(null);
  const [showParams, setShowParams] = useState(false);
  const [compareMode, setCompareMode] = useState(null); // null | 'coldgas' | 'ion'
  const [orbitShiftMM, setOrbitShiftMM] = useState(100);

  // Derived
  const tCycle   = p.F_thrust * p.dt_thrust / p.F_drag;
  const dtRepulse  = p.tau_coil * 8;
  const vRelS1     = p.F_absorb * (1/p.m_emms + 1/p.m_hull) * dtRepulse;
  const dtCoastStr = Math.max(p.L_emms / Math.max(vRelS1, 0.01), 0.05);
  const dtDamp     = 0.12;
  const dtColdGas  = p.J_mib / p.F_reset;          // MIB burn time
  const dtMassCoast = p.L_emms * p.m_emms / p.J_mib; // coast time to repulsion wall
  const realDurs = [tCycle, dtRepulse, dtCoastStr, dtDamp, dtColdGas, dtMassCoast];
  const J_pulse  = p.F_thrust * p.dt_thrust;
  const propCycle = (p.m_hull + p.m_emms) * (1 - Math.exp(-J_pulse / (p.isp * G0)));
  const propDay   = propCycle * 86400 / tCycle;

  const initState = useCallback(() => ({
    phase: 0, tWall: 0, tReal: 0, tAbs: 0,
    xCom: 0, vCom: 0,
    xRel: paramsRef.current.L_emms / 2,  // mass starts at repulsion wall (+x, right)
    vRel: 0,
    vHull: 0, aHull: 0, vMass: 0, aMass: 0, aCom: 0,
    fe: 0, ft: 0, maxXRel: 0, cycles: 0,
  }), []);

  useEffect(() => { sim.current = initState(); }, [initState]);
  useEffect(() => { paramsRef.current = p; }, [p]);

  // ── Draw ────────────────────────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const s = sim.current;
    if (!canvas || !s) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    const ph = PH[s.phase];
    const pr = paramsRef.current;
    const M = pr.m_hull + pr.m_emms;

    // Background
    ctx.fillStyle = C.bg; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = ph.color + '0a'; ctx.fillRect(0, 0, W, H);

    // Subtle grid
    ctx.strokeStyle = C.border + '30'; ctx.lineWidth = 0.5;
    for (let x = 0; x < W; x += 50) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
    }

    // Layout — hull is wide enough to fully contain the mass at its travel extremes
    const CY = H * 0.40;
    // Hull sized so EMMS_RANGE fits inside: HULL_W = L_hull * scale, capped
    const HULL_W = Math.min(pr.L_hull * 900, 340);
    const HULL_H = 58;
    const MASS_W = 22;
    const MASS_H = 38;
    // EMMS_RANGE: mass center travels ±EMMS_RANGE px inside hull.
    // Set so mass block JUST touches the hull inner walls at extremes.
    const EMMS_RANGE = Math.floor(HULL_W / 2 - MASS_W / 2 - 3);

    // Scale: hull starts near left edge, drifts right — fit N_VIEW cycles across canvas width.
    // cycleStroke = CoM displacement per cycle ≈ (m_emms/M)*L_emms (cold gas drives xCom rightward)
    const N_VIEW     = 12;
    const cycleStroke = Math.max((pr.m_emms / M) * pr.L_emms, 1e-6);
    const CX_START   = W * 0.06;   // hull starts here (left margin)
    const visualScale = (W * 0.86) / (N_VIEW * cycleStroke);

    // x_disp: hull display position (0 = initial rest; increases by cycleStroke each cycle)
    // At xRel=+L/2 (rest):  x_disp = xCom  → hull at CX_START + xCom*visualScale ✓
    // At xRel=−L/2 (damper): x_disp = xCom+(m_emms/M)*L_emms → hull moved right by cycleStroke ✓
    const x_disp = s.xCom - (pr.m_emms / M) * (s.xRel - pr.L_emms / 2);
    const hullCX = CX_START + x_disp * visualScale;
    // Mass center inside hull
    const massCX = hullCX + s.xRel * (EMMS_RANGE / (pr.L_emms / 2));

    // ── Cycle position marks (thin vertical lines at each cycle boundary) ──────
    ctx.setLineDash([2, 5]);
    for (let n = 0; n <= N_VIEW; n++) {
      const mx = CX_START + n * cycleStroke * visualScale;
      if (mx > W - 2) break;
      ctx.strokeStyle = n === 0 ? C.muted + '60' : C.border + '50';
      ctx.lineWidth = n === 0 ? 1.2 : 0.6;
      ctx.beginPath(); ctx.moveTo(mx, CY - HULL_H / 2 - 22); ctx.lineTo(mx, CY + HULL_H / 2 + 36); ctx.stroke();
      ctx.font = `9px ${MFONT}`; ctx.fillStyle = C.muted + '80'; ctx.textAlign = 'center';
      ctx.fillText(`${n}`, mx, CY - HULL_H / 2 - 26);
    }
    ctx.setLineDash([]);

    // Orbit baseline
    const baseY = CY + HULL_H / 2 + 32;
    ctx.strokeStyle = C.muted + '30'; ctx.lineWidth = 0.8;
    ctx.beginPath(); ctx.moveTo(CX_START, baseY); ctx.lineTo(W - 10, baseY); ctx.stroke();

    // ── Hull displacement (cyan) — instantaneous hull position in inertial frame ──
    // x_disp = xCom − (m_emms/M)·(xRel − L/2). Shows net hull orbital advance per cycle.
    if (x_disp > cycleStroke * 0.01) {
      ctx.strokeStyle = '#0e9cc055'; ctx.lineWidth = 1; ctx.setLineDash([4, 4]);
      ctx.beginPath(); ctx.moveTo(CX_START, baseY - 4);
      ctx.lineTo(hullCX, baseY - 4); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = '#0e9cc0';
      ctx.beginPath();
      ctx.moveTo(hullCX - 5, CY + HULL_H / 2 + 6);
      ctx.lineTo(hullCX + 5, CY + HULL_H / 2 + 6);
      ctx.lineTo(hullCX, CY + HULL_H / 2 + 14);
      ctx.closePath(); ctx.fill();
      ctx.font = `10px ${MFONT}`; ctx.fillStyle = '#0e9cc0'; ctx.textAlign = 'center';
      ctx.fillText(`Hull Δ = ${(x_disp * 1000).toFixed(3)} mm`, hullCX, baseY + 14);
    }

    // Solar panels
    [[hullCX - HULL_W / 2 - 30, CY - 8], [hullCX + HULL_W / 2, CY - 8]].forEach(([sx, sy]) => {
      ctx.fillStyle = '#0e9cc018'; ctx.strokeStyle = '#0e9cc040'; ctx.lineWidth = 0.8;
      ctx.fillRect(sx, sy, 28, 16); ctx.strokeRect(sx, sy, 28, 16);
      [1, 2].forEach(i => {
        ctx.beginPath(); ctx.moveTo(sx + i * 28 / 3, sy); ctx.lineTo(sx + i * 28 / 3, sy + 16); ctx.stroke();
      });
    });

    // Hull body
    rr(ctx, hullCX - HULL_W / 2, CY - HULL_H / 2, HULL_W, HULL_H, 7);
    ctx.fillStyle = C.panel; ctx.fill();
    ctx.strokeStyle = '#0e9cc0'; ctx.lineWidth = 1.5; ctx.stroke();

    // Hull label
    ctx.font = `10px ${MFONT}`; ctx.fillStyle = '#0e9cc0'; ctx.textAlign = 'center';
    ctx.fillText(`HULL  ${pr.m_hull} kg`, hullCX, CY - HULL_H / 2 - 8);

    // ── EMMS track line (inside hull, from damper wall to repulsion wall) ─────
    ctx.strokeStyle = C.border + '50'; ctx.lineWidth = 0.8; ctx.setLineDash([3, 4]);
    ctx.beginPath();
    ctx.moveTo(hullCX - EMMS_RANGE, CY);
    ctx.lineTo(hullCX + EMMS_RANGE, CY);
    ctx.stroke();
    ctx.setLineDash([]);

    // ── DAMPER wall (left, −x) ─────────────────────────────────────────────────
    const damperX   = hullCX - EMMS_RANGE - MASS_W / 2;  // left edge of mass at extreme
    // Damper glows when mass is at or approaching damper (S2 coast → S3 impact)
    const damperActive = s.phase === 3 || (s.phase === 2 && s.xRel < -pr.L_emms * 0.3);
    ctx.strokeStyle = damperActive ? '#fbbf24' : '#fbbf2455';
    ctx.lineWidth = damperActive ? 2.5 : 1.5;
    ctx.beginPath();
    ctx.moveTo(hullCX - HULL_W / 2 + 1, CY - HULL_H / 2 + 5);
    ctx.lineTo(hullCX - HULL_W / 2 + 1, CY + HULL_H / 2 - 5);
    ctx.stroke();
    // Damper teeth (sawtooth indicating spring/damper)
    ctx.strokeStyle = damperActive ? '#fbbf24' : '#fbbf2440';
    ctx.lineWidth = 1;
    for (let ky = CY - HULL_H / 2 + 8; ky < CY + HULL_H / 2 - 4; ky += 9) {
      ctx.beginPath();
      ctx.moveTo(hullCX - HULL_W / 2 + 1, ky);
      ctx.lineTo(hullCX - HULL_W / 2 + 7, ky + 4);
      ctx.stroke();
    }
    ctx.font = `8px ${MFONT}`; ctx.textAlign = 'left';
    ctx.fillStyle = damperActive ? '#fbbf24' : '#fbbf2480';
    ctx.fillText('DAMPER', hullCX - HULL_W / 2 + 4, CY - HULL_H / 2 + 15);
    ctx.fillText(`−${(pr.L_emms / 2 * 1000).toFixed(0)}mm`, hullCX - HULL_W / 2 + 4, CY - HULL_H / 2 + 25);

    // ── REPULSION wall (right, +x) ────────────────────────────────────────────
    const repActive  = s.phase === 1;  // EMMS fires only in S1 (repulsion stroke)
    const repResting = s.phase === 0 || s.phase === 5;
    ctx.strokeStyle = repActive ? '#0e9cc0' : repResting ? '#0e9cc060' : '#0e9cc030';
    ctx.lineWidth = repActive ? 2.5 : 1.5;
    ctx.beginPath();
    ctx.moveTo(hullCX + HULL_W / 2 - 1, CY - HULL_H / 2 + 5);
    ctx.lineTo(hullCX + HULL_W / 2 - 1, CY + HULL_H / 2 - 5);
    ctx.stroke();
    // Coil symbol (zigzag)
    ctx.strokeStyle = repActive ? '#0e9cc0' : '#0e9cc040';
    ctx.lineWidth = 1;
    ctx.beginPath();
    let first = true;
    for (let ky = CY - 18; ky < CY + 18; ky += 7) {
      const xz = hullCX + HULL_W / 2 - 1 - (Math.floor((ky - (CY - 18)) / 7) % 2 === 0 ? 5 : 0);
      if (first) { ctx.moveTo(xz, ky); first = false; } else { ctx.lineTo(xz, ky); }
    }
    ctx.stroke();
    ctx.font = `8px ${MFONT}`; ctx.textAlign = 'right';
    ctx.fillStyle = repActive ? '#0e9cc0' : repResting ? '#0e9cc080' : '#0e9cc050';
    ctx.fillText('REPULSION', hullCX + HULL_W / 2 - 4, CY - HULL_H / 2 + 15);
    ctx.fillText(`+${(pr.L_emms / 2 * 1000).toFixed(0)}mm`, hullCX + HULL_W / 2 - 4, CY - HULL_H / 2 + 25);

    // ── Moving mass block ─────────────────────────────────────────────────────
    const massColor = s.phase === 1 ? '#0e9cc0'   // S1: EMMS repulsion stroke
                    : s.phase === 2 ? '#0e9cc080' // S2: coasting (faded cyan — from EMMS momentum)
                    : s.phase === 3 ? '#fbbf24'   // S3: at damper
                    : s.phase === 4 ? '#a855f7'   // S4: cold gas stroke
                    : s.phase === 5 ? '#f59e0b'   // S5: mass coasting alone
                    : '#22c55e';                  // S0: idle at repulsion wall

    // Mass block — constrained to stay inside hull by EMMS_RANGE formula
    rr(ctx, massCX - MASS_W / 2, CY - MASS_H / 2, MASS_W, MASS_H, 3);
    ctx.fillStyle = massColor + '35'; ctx.fill();
    ctx.strokeStyle = massColor; ctx.lineWidth = 2; ctx.stroke();

    // Mass labels
    ctx.font = `10px ${MFONT}`; ctx.fillStyle = massColor; ctx.textAlign = 'center';
    ctx.fillText(`${pr.m_emms}kg`, massCX, CY + 4);
    ctx.font = `9px ${MFONT}`;
    ctx.fillText(`${(s.xRel * 1000).toFixed(1)}mm`, massCX, CY - MASS_H / 2 - 5);

    // ── S4: MIB cold gas exhaust visualization ────────────────────────────────
    if (s.phase === 4) {
      const nozzleX = massCX - MASS_W / 2;
      const nozzleY = CY;
      // Nozzle hot glow
      const grd = ctx.createRadialGradient(nozzleX, nozzleY, 1, nozzleX - 5, nozzleY, 22);
      grd.addColorStop(0, '#a855f7cc');
      grd.addColorStop(0.4, '#a855f760');
      grd.addColorStop(1, '#a855f700');
      ctx.fillStyle = grd;
      ctx.beginPath(); ctx.arc(nozzleX - 5, nozzleY, 22, 0, Math.PI * 2); ctx.fill();
      // Exhaust jet — propellant particles streaming left
      const t = s.tAbs * 18;
      for (let i = 0; i < 8; i++) {
        const phase_i = (t + i * 0.7) % 1;
        const dist = 6 + phase_i * 32;
        const spread = Math.sin(s.tAbs * 25 + i * 1.7) * 5;
        const alpha = (1 - phase_i) * 0.85;
        const r = 1.5 * (1 - phase_i * 0.5);
        ctx.beginPath();
        ctx.arc(nozzleX - dist, nozzleY + spread, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(168,85,247,${alpha.toFixed(2)})`;
        ctx.fill();
      }
      // MIB label above mass
      const burnMs = (pr.J_mib / pr.F_reset * 1000).toFixed(0);
      ctx.font = `bold 11px ${MFONT}`; ctx.fillStyle = '#a855f7'; ctx.textAlign = 'center';
      ctx.fillText(`MIB = ${(pr.J_mib * 1000).toFixed(1)} mNs`, massCX, CY - MASS_H / 2 - 18);
      ctx.font = `10px ${MFONT}`; ctx.fillStyle = '#a855f7cc';
      ctx.fillText(`burn: ${burnMs} ms`, massCX, CY - MASS_H / 2 - 6);
      // Impulse arrow on mass pointing right (+x)
      arrow(ctx, nozzleX - 38, nozzleY, nozzleX, nozzleY, '#a855f7', 2);
    }

    // Motion trail dots to show mass is moving
    if (Math.abs(s.vRel) > 0.002) {
      const trailDir = s.vRel > 0 ? 1 : -1;
      const pxPerRel = EMMS_RANGE / (pr.L_emms / 2);
      for (let ti = 1; ti <= 3; ti++) {
        const tx = massCX - trailDir * ti * 10;
        const alpha = (4 - ti) / 4;
        ctx.beginPath();
        ctx.arc(tx, CY, 2, 0, Math.PI * 2);
        ctx.fillStyle = massColor + Math.floor(alpha * 80).toString(16).padStart(2, '0');
        ctx.fill();
      }
    }

    // ── Velocity arrows (above hull) ──────────────────────────────────────────
    const vScale = 8e5;
    const vY = CY - HULL_H / 2 - 18;
    const vHullPx = s.vHull * vScale;
    const vMassPx = s.vMass * vScale;
    if (Math.abs(vHullPx) > 0.5) {
      arrow(ctx, hullCX, vY, hullCX + vHullPx, vY, '#e2e8f0', 1.5);
      ctx.font = `9px ${MFONT}`; ctx.fillStyle = '#e2e8f0';
      ctx.textAlign = vHullPx > 0 ? 'left' : 'right';
      ctx.fillText(`v_hull=${(s.vHull * 1000).toFixed(2)}mm/s`, hullCX + vHullPx + (vHullPx > 0 ? 3 : -3), vY - 3);
    }
    if (Math.abs(vMassPx) > 0.5 && Math.abs(vMassPx - vHullPx) > 2) {
      arrow(ctx, massCX, vY - 12, massCX + vMassPx, vY - 12, massColor, 1.5);
      ctx.font = `9px ${MFONT}`; ctx.fillStyle = massColor;
      ctx.textAlign = vMassPx > 0 ? 'left' : 'right';
      ctx.fillText(`v_mass=${(s.vMass * 1000).toFixed(2)}mm/s`, massCX + vMassPx + (vMassPx > 0 ? 3 : -3), vY - 15);
    }

    // ── Force arrows (below hull) ─────────────────────────────────────────────
    const arrowY = CY + HULL_H / 2 + 12;

    // Drag (always, pointing −x)
    const dragPx = 40;
    arrow(ctx, hullCX - HULL_W / 4 + dragPx, arrowY, hullCX - HULL_W / 4, arrowY, '#ef4444', 2);
    ctx.font = `9px ${MFONT}`; ctx.fillStyle = '#ef4444'; ctx.textAlign = 'center';
    ctx.fillText(`${(pr.F_drag * 1e6).toFixed(0)}μN drag`, hullCX - HULL_W / 4 + dragPx / 2, arrowY - 7);

    // S4: mass-only cold gas — arrow on MASS pointing +x; hull has NO arrow
    // S4: mass-only cold gas — arrow on MASS pointing +x toward repulsion
    if (s.phase === 4 && s.ft > 0) {
      const thrPx = Math.max(32, s.ft * 8000);
      arrow(ctx, massCX - thrPx, arrowY - 2, massCX, arrowY - 2, '#a855f7', 2);
      ctx.font = `9px ${MFONT}`; ctx.fillStyle = '#a855f7'; ctx.textAlign = 'center';
      ctx.fillText(`${(s.ft * 1000).toFixed(1)}mN  MASS only`, massCX - thrPx / 2, arrowY - 11);
    }
    // S5: mass coasting — show mass velocity arrow pointing right
    else if (s.phase === 5 && Math.abs(s.vRel) > 0.001) {
      const vRelPx = Math.min(50, Math.max(20, s.vRel * 200));
      arrow(ctx, massCX, arrowY - 2, massCX + vRelPx, arrowY - 2, '#f59e0b', 2);
      ctx.font = `9px ${MFONT}`; ctx.fillStyle = '#f59e0b'; ctx.textAlign = 'left';
      ctx.fillText(`mass coasting →`, massCX + vRelPx + 4, arrowY - 8);
    }
    // S1: hull thruster + EMMS active
    else if (s.ft > 0) {
      const thrPx = Math.max(35, s.ft * 6000);
      arrow(ctx, hullCX + HULL_W / 4, arrowY, hullCX + HULL_W / 4 + thrPx, arrowY, '#0e9cc0', 2);
      ctx.font = `9px ${MFONT}`; ctx.fillStyle = '#0e9cc0'; ctx.textAlign = 'center';
      ctx.fillText(`${(s.ft * 1000).toFixed(1)}mN thrust`, hullCX + HULL_W / 4 + thrPx / 2, arrowY - 7);
    }

    // EMMS internal force arrows (S1 only; shows action on mass and reaction on hull)
    if (Math.abs(s.fe) > 0.001) {
      const emmsPx = Math.max(24, Math.abs(s.fe) * 90);
      const dir = s.fe > 0 ? 1 : -1;
      // Mass pushed toward damper (−x in S1)
      arrow(ctx, massCX, CY + 5, massCX + dir * emmsPx, CY + 5, massColor, 2);
      ctx.font = `9px ${MFONT}`; ctx.fillStyle = massColor; ctx.textAlign = 'center';
      ctx.fillText(`${(Math.abs(s.fe)*1000).toFixed(0)}mN repulsion`, massCX + dir * emmsPx / 2, CY + 17);
      // Hull reaction: RADIALLY OUTWARD (+x in S1) — bright prominent arrow
      const hullEndX = hullCX - dir * emmsPx;
      arrow(ctx, hullCX, CY - 7, hullEndX, CY - 7, '#00ff88', 3);
      ctx.font = `bold 10px ${MFONT}`; ctx.fillStyle = '#00ff88'; ctx.textAlign = 'center';
      ctx.fillText('HULL → RADIALLY OUTWARD', (hullCX + hullEndX) / 2, CY - 19);
    }

    // S3: Damping impact — hull receives inward impulse as mass momentum transfers via damper
    if (s.phase === 3) {
      const fade = Math.max(0, 1 - s.tWall / (PH_WALL[3] * 0.75));
      const impPx = 64;
      const col = `rgba(251,191,36,${fade.toFixed(2)})`;
      arrow(ctx, hullCX, CY - 7, hullCX - impPx, CY - 7, col, 3);
      ctx.font = `bold 10px ${MFONT}`; ctx.fillStyle = col; ctx.textAlign = 'center';
      ctx.fillText('HULL ← RADIALLY INWARD', hullCX - impPx / 2, CY - 19);
    }

    // S2 coasting label — show both arrows labeled
    if (s.phase === 2 && Math.abs(s.vRel) > 0.001) {
      ctx.font = `9px ${MFONT}`; ctx.fillStyle = '#0e9cc0'; ctx.textAlign = 'center';
      ctx.fillText('COASTING  ↔  opposite dirs', hullCX, CY + HULL_H / 2 + 28);
    } else if (Math.abs(s.aHull) > 1e-6) {
      ctx.font = `9px ${MFONT}`; ctx.fillStyle = '#e2e8f0'; ctx.textAlign = 'center';
      ctx.fillText(`a_hull = ${(s.aHull * 1000).toFixed(1)} mm/s²`, hullCX, CY + HULL_H / 2 + 28);
    }

    // ── Phase progress bar (bottom) ────────────────────────────────────────────
    const barH = 20, barY = H - barH;
    const phWallDraw = s.phase === 5 ? (pr.t_coast_wall ?? 0.5) : PH_WALL[s.phase];
    const prog = Math.min(s.tWall / phWallDraw, 1);
    ctx.fillStyle = ph.color + '15'; ctx.fillRect(0, barY, W, barH);
    ctx.fillStyle = ph.color + '50'; ctx.fillRect(0, barY, W * prog, barH);
    ctx.font = `10px ${MFONT}`; ctx.fillStyle = ph.color; ctx.textAlign = 'center';
    ctx.fillText(`${ph.name}  —  cycle ${s.cycles}`, W / 2, barY + 14);
  }, [C]);


  // ── Simulation advance ─────────────────────────────────────────────────────
  const advance = useCallback((dtWall) => {
    const s = sim.current;
    const pr = paramsRef.current;
    if (!s || !pr) return;
    // Real-time durations for each phase
    const tCycleLocal  = pr.F_thrust * pr.dt_thrust / pr.F_drag;
    // S1: EMMS repulsion stroke — fires for tau_coil*8 (ramp + sustain)
    const dtRepulse    = pr.tau_coil * 8;
    // S2: coasting stroke — after impulse, mass coasts L_emms at velocity from S1
    //     v_rel_S1 ≈ F_absorb*(1/m_emms+1/m_hull)*dtRepulse; time to cross rest of L_emms
    const vRelAfterS1  = pr.F_absorb * (1/pr.m_emms + 1/pr.m_hull) * dtRepulse;
    const dtCoastStroke = Math.max(pr.L_emms / Math.max(vRelAfterS1, 0.01), 0.05);
    // S3: damping stroke (short — inelastic stop at damper wall)
    const dtDamp       = 0.12;
    // S4: MIB cold gas fires briefly — burn time only (mass then coasts in S5)
    const dtColdGas    = pr.J_mib / pr.F_reset;
    // S5: mass coasts from damper side to repulsion wall at v = J_mib/m_emms
    const dtMassCoast  = pr.L_emms * pr.m_emms / pr.J_mib;
    const realDursLocal = [tCycleLocal, dtRepulse, dtCoastStroke, dtDamp, dtColdGas, dtMassCoast];
    // S5 wall-time is configurable; all other phases use the fixed PH_WALL constants
    const phWall = (ph) => ph === 5 ? (pr.t_coast_wall ?? 0.5) : PH_WALL[ph];
    const speed  = realDursLocal[s.phase] / phWall(s.phase);
    const dtReal = dtWall * speed;
    const DT     = Math.min(dtReal / 20, 2e-4);
    // Coasting phases (no forces) are linear → 1-3 substeps is exact; cap active phases at 15
    const isCoasting = (s.phase === 2 || s.phase === 5);
    const n = isCoasting
      ? Math.min(3, Math.max(1, Math.round(dtReal / DT)))
      : Math.min(15, Math.max(1, Math.round(dtReal / DT)));
    let ns = { ...s, tReal: s.tReal + dtReal, tWall: s.tWall + dtWall };
    for (let i = 0; i < n; i++) ns = physStep(ns, dtReal / n, pr);
    if (ns.tWall >= phWall(ns.phase)) {
      const next = (ns.phase + 1) % 6;
      ns = { ...ns, phase: next, tWall: 0, tReal: 0 };
      if (next === 4) {
        // S3→S4: damper absorbed ALL momentum — both hull and mass stop; mass at damper wall.
        ns = { ...ns, vCom: 0, vRel: 0, xRel: -pr.L_emms / 2 };
      }
      if (next === 5) {
        // S4→S5: brief MIB burn complete. Mass coasting with acquired vRel toward repulsion wall.
        // xRel and vRel carry over naturally from physStep — do NOT teleport.
        // Hull is frozen (vHull ≈ 0); only mass moves.
      }
      if (next === 0) {
        // S5→S0: xCom has accumulated the S5 drift (body coasted forward).
        // NOW freeze vCom=0 so hull holds this new position.
        // Each cycle starts from rest → same forces → same displacement per cycle.
        ns = { ...ns, cycles: ns.cycles + 1, vCom: 0, vRel: 0, xRel: pr.L_emms / 2 };
      }
    }
    sim.current = ns;
  }, []);

  // ── RAF loop ───────────────────────────────────────────────────────────────
  const loop = useCallback((ts) => {
    const dtWall = lastTs.current ? Math.min((ts - lastTs.current) / 1000, 0.05) : 0.016;
    lastTs.current = ts;
    advance(dtWall);
    draw();
    frameCount.current++;
    // Throttle React state updates to every 4 frames — canvas draws every frame unaffected
    if (frameCount.current % 4 === 0) {
      const s = sim.current;
      const pr_t = paramsRef.current;
      const M_t  = pr_t.m_hull + pr_t.m_emms;
      const xHull_disp = s.xCom - (pr_t.m_emms / M_t) * (s.xRel - pr_t.L_emms / 2);
      setTele({
        phase: s.phase, tAbs: s.tAbs, tReal: s.tReal,
        vHull: s.vHull, aHull: s.aHull,
        vMass: s.vMass, aMass: s.aMass, vCom: s.vCom,
        xRel: s.xRel, xCom: s.xCom,
        xHull: xHull_disp,
        fe: s.fe, ft: s.ft,
        cycles: s.cycles, maxXRel: s.maxXRel,
      });
    }
    raf.current = requestAnimationFrame(loop);
  }, [advance, draw]);

  useEffect(() => {
    if (running) {
      lastTs.current = null;
      raf.current = requestAnimationFrame(loop);
    } else {
      cancelAnimationFrame(raf.current);
    }
    return () => cancelAnimationFrame(raf.current);
  }, [running, loop]);

  useEffect(() => { if (!running) { draw(); } }, [draw, p, running]);
  const reset = () => { sim.current = initState(); draw(); setTele(null); };

  // ── Param input ────────────────────────────────────────────────────────────
  function ParamRow({ rows }) {
    return (
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {rows.map(({ label, key, unit, mul = 1, step = 0.1, min = 0 }) => (
          <div key={key} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <div style={{ ...mono, fontSize: 7, color: C.muted, letterSpacing: 1.5 }}>{label}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <input
                type="number" min={min} step={step}
                value={+((p[key] * mul).toPrecision(4))}
                onChange={e => {
                  const v = +e.target.value / mul;
                  setP(prev => ({ ...prev, [key]: v }));
                }}
                style={{
                  width: 68, ...mono, fontSize: 11, padding: "3px 6px",
                  background: C.eqBg, border: `1px solid ${C.border}`,
                  color: C.text, borderRadius: 4, textAlign: "right",
                }}
              />
              <span style={{ ...mono, fontSize: 9, color: C.muted }}>{unit}</span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const ph = PH[tele?.phase ?? 0];
  const propPerCycleG = propCycle * 1000;
  const propPerDayG   = propDay * 1000;
  const propPerYearG  = propPerDayG * 365.25;

  // ── S4: mass cold-gas reset propellant (moves m_emms from damper → repulsion wall) ──
  const propMassReset  = p.m_emms * (1 - Math.exp(-p.J_mib / (p.isp * G0)));
  const propTotalCycle = propCycle + propMassReset;
  const maxDispMM     = (tele?.maxXRel ?? 0) * 1000;
  const netDisplaceMM = (tele?.xHull ?? 0) * 1000;
  const cyclesPerDay  = 86400 / tCycle;

  // ── Real physics duration of each stroke ────────────────────────────────────
  // S0: stable hold — drag decelerates hull until the next correction is needed
  //     Time = J_hull_pulse / F_drag  (how long the tiny hull-thruster impulse lasts against drag)
  const t_s0 = (p.F_thrust * p.dt_thrust) / p.F_drag;
  // S1: EMMS repulsion stroke — coil fires for tau_coil × 8 (ramp + sustain window)
  const t_s1 = p.tau_coil * 8;
  // S2: coast to damper — mass travels full L_emms at the velocity acquired from S1
  const vRelAfterS1 = p.F_absorb * (1 / p.m_emms + 1 / p.m_hull) * t_s1;
  const t_s2 = Math.max(p.L_emms / Math.max(vRelAfterS1, 0.001), 0.05);
  // S3: inelastic damping — fixed short collision window
  const t_s3 = 0.12;
  // S4: cold gas MIB burn on mass — ends when J_mib is delivered
  const t_s4 = p.J_mib / p.F_reset;
  // S5: mass coasts from damper to repulsion wall at v = J_mib / m_emms
  const t_s5 = p.L_emms * p.m_emms / p.J_mib;
  // Full cycle time — S5 uses user's mission-selected coast window (t_coast_wall).
  // Physics S5 = t_s5 (32s real coast); mission can compress this to any wall-time.
  const realCycleTime   = t_s0 + t_s1 + t_s2 + t_s3 + t_s4 + p.t_coast_wall;
  const totalMissionTime = realCycleTime * (tele?.cycles ?? 0);

  return (
    <div id="cycle-page">
      <style>{`
        #cycle-page { padding: 0 4px; box-sizing: border-box; }
        .cy-sec-header { display: flex; align-items: center; gap: 10px; margin: 28px 0 14px; }
        .cy-sec-dot  { width: 3px; height: 18px; border-radius: 2px; flex-shrink: 0; }
        .cy-sec-label { font-family: 'JetBrains Mono', monospace; font-size: 8px; letter-spacing: 3px; font-weight: 700; }
        .cy-sec-line { flex: 1; height: 1px; }
        .cy-tele-grid  { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 12px; }
        .cy-results-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 12px; }
        .cy-stroke-grid  { display: grid; grid-template-columns: repeat(6, 1fr); gap: 6px; }
        .cy-mission-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 12px; }
        .cy-prop-summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
        @media (max-width: 640px) {
          .cy-tele-grid   { grid-template-columns: 1fr !important; }
          .cy-results-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .cy-stroke-grid  { grid-template-columns: repeat(3, 1fr) !important; }
          .cy-mission-grid { grid-template-columns: 1fr !important; }
          .cy-prop-budget  { grid-template-columns: 1fr !important; }
          .cy-prop-summary { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 400px) {
          .cy-results-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .cy-stroke-grid  { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
      {/* ── Controls ── */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <button onClick={() => setRunning(r => !r)}
          style={{
            ...mono, fontSize: 9, padding: "7px 18px", borderRadius: 6, cursor: "pointer",
            background: running ? "#ef444420" : "#22c55e20",
            border: `1px solid ${running ? "#ef444466" : "#22c55e66"}`,
            color: running ? "#ef4444" : "#22c55e", letterSpacing: 1.5,
          }}>
          {running ? "■ PAUSE" : "▶ PLAY"}
        </button>
        <button onClick={reset}
          style={{
            ...mono, fontSize: 9, padding: "7px 16px", borderRadius: 6, cursor: "pointer",
            background: "transparent", border: `1px solid ${C.border}`, color: C.muted,
          }}>
          ⟳ RESET
        </button>
        <button onClick={() => setShowParams(s => !s)}
          style={{
            ...mono, fontSize: 9, padding: "7px 16px", borderRadius: 6, cursor: "pointer",
            background: showParams ? "#0e9cc020" : "transparent",
            border: `1px solid ${showParams ? "#0e9cc055" : C.border}`,
            color: showParams ? "#0e9cc0" : C.muted,
          }}>
          {showParams ? "▲ HIDE PARAMS" : "⚙ EDIT PARAMS"}
        </button>
        {/* Phase chips */}
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {PH.map((ph2, i) => (
            <div key={i} style={{
              ...mono, fontSize: 7, padding: "3px 8px", borderRadius: 10,
              background: (tele?.phase ?? 0) === i ? `${ph2.color}22` : "transparent",
              border: `1px solid ${(tele?.phase ?? 0) === i ? ph2.color + "66" : C.border}`,
              color: (tele?.phase ?? 0) === i ? ph2.color : C.muted,
            }}>S{i}</div>
          ))}
        </div>
      </div>

      {/* ── Parameters panel ── */}
      {showParams && (
        <div style={{
          background: C.panel, border: `1px solid ${C.border}`,
          borderRadius: 8, padding: 16, marginBottom: 12,
        }}>
          <div style={{ ...mono, fontSize: 8, color: C.muted, letterSpacing: 2, marginBottom: 12 }}>
            PHASE PARAMETERS
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <ParamRow rows={[
              { label: "HULL MASS",       key: "m_hull",    unit: "kg",  step: 0.5 },
              { label: "EMMS MASS",       key: "m_emms",    unit: "kg",  step: 0.1 },
              { label: "HULL LENGTH",     key: "L_hull",    mul: 100, unit: "cm", step: 1 },
              { label: "EMMS TRAVEL (±)", key: "L_emms",    mul: 1000, unit: "mm", step: 1 },
            ]} />
            <ParamRow rows={[
              { label: "REPULSION FORCE", key: "F_absorb",  mul: 1000, unit: "mN", step: 10 },
              { label: "RESET FORCE",     key: "F_reset",   mul: 1000, unit: "mN", step: 1 },
              { label: "THRUST FORCE",    key: "F_thrust",  mul: 1000, unit: "mN", step: 0.5 },
              { label: "VALVE OPEN TIME", key: "dt_thrust", mul: 1000, unit: "ms", step: 0.1 },
            ]} />
            <ParamRow rows={[
              { label: "DRAG FORCE",     key: "F_drag",       mul: 1e6,  unit: "μN",  step: 1 },
              { label: "COIL τ",        key: "tau_coil",     mul: 1000, unit: "ms",  step: 0.5 },
              { label: "ISP",           key: "isp",          unit: "s",  step: 5 },
              { label: "MIB",           key: "J_mib",        mul: 1000, unit: "mNs", step: 0.1, min: 0.01 },
              { label: "S5 COAST ANIM", key: "t_coast_wall", mul: 1000, unit: "ms",  step: 100, min: 0.1 },
            ]} />
          </div>
        </div>
      )}

      {/* ── Canvas ── */}
      <div style={{
        background: C.panel, border: `2px solid ${ph?.color ?? "#0e9cc0"}33`,
        borderTop: `2px solid ${ph?.color ?? "#0e9cc0"}88`,
        borderRadius: 10, overflow: "hidden", marginBottom: 12,
      }}>
        <canvas
          ref={canvasRef}
          width={820} height={300}
          style={{ width: "100%", height: "auto", display: "block" }}
        />
      </div>

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* ── Section: Propellant Budget ── */}
      {/* ══════════════════════════════════════════════════════════════ */}
      <div className="cy-sec-header">
        <div className="cy-sec-dot" style={{ background: '#f59e0b' }} />
        <span className="cy-sec-label" style={{ color: '#f59e0b' }}>PROPELLANT PER CYCLE</span>
        <div className="cy-sec-line" style={{ background: 'linear-gradient(90deg, #f59e0b40, transparent)' }} />
      </div>

      {/* ── Compact propellant table ── */}
      <div style={{
        background: C.eqBg, border: `1px solid ${C.border}`,
        borderRadius: 8, padding: "14px 18px", marginBottom: 20,
      }}>
        {/* S4 row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          paddingBottom: 10, borderBottom: `1px solid ${C.border}30`, marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <span style={{ ...mono, fontSize: 8, color: '#a855f7', letterSpacing: 2 }}>S4  EMMS MASS RESET</span>
            <div style={{ ...mono, fontSize: 12, color: C.text, marginTop: 5 }}>
              <span style={{ color: '#a855f7', fontWeight: 700 }}>{p.m_emms.toFixed(1)} kg</span>
              {' '}moves{' '}
              <span style={{ color: '#0e9cc0', fontWeight: 700 }}>{(p.L_emms * 1000).toFixed(0)} mm</span>
              {' '}in{' '}
              <input
                type="number" min="0.5" max="1000" step="0.5"
                value={+(p.L_emms * p.m_emms / p.J_mib).toFixed(1)}
                onChange={e => {
                  const tc = parseFloat(e.target.value);
                  if (tc > 0.1) setP(prev => ({ ...prev, J_mib: prev.L_emms * prev.m_emms / tc }));
                }}
                style={{
                  width: 52, padding: '2px 5px', ...mono, fontSize: 12,
                  background: C.panel, border: `1px solid #a855f755`,
                  color: '#a855f7', borderRadius: 4, textAlign: 'right',
                }}
              />
              {' '}<span style={{ color: C.muted }}>s</span>
              {' '}·{' '}
              <span style={{ color: C.muted, fontSize: 11 }}>
                {(p.J_mib / p.F_reset * 1000).toFixed(0)} ms burn  ·  J = {(p.J_mib * 1000).toFixed(3)} mNs
              </span>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ ...mono, fontSize: 16, color: '#a855f7', fontWeight: 700 }}>
              {(propMassReset * 1e6).toFixed(6)}
            </span>
            <span style={{ ...mono, fontSize: 10, color: C.muted, marginLeft: 4 }}>mg / reset</span>
            <div style={{ ...mono, fontSize: 9, color: C.muted }}>
              Δm = m × (1 − e^(−J/(Isp·g₀)))
            </div>
          </div>
        </div>

        {/* S1 row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          paddingBottom: 10, borderBottom: `1px solid ${C.border}30`, marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <span style={{ ...mono, fontSize: 8, color: '#0e9cc0', letterSpacing: 2 }}>S1  HULL DRAG CORRECTION</span>
            <div style={{ ...mono, fontSize: 12, color: C.text, marginTop: 5 }}>
              <span style={{ color: '#0e9cc0', fontWeight: 700 }}>{(p.m_hull + p.m_emms).toFixed(1)} kg</span>
              {' '}hull · every{' '}
              <span style={{ color: '#0e9cc0', fontWeight: 700 }}>{tCycle.toFixed(4)} s</span>
              {' '}·{' '}
              <span style={{ color: '#22c55e' }}>{(p.dt_thrust * 1000).toFixed(2)} ms</span>
              {' '}valve  ·  cancels{' '}
              <span style={{ color: '#ef4444' }}>{(p.F_drag * 1e6).toFixed(1)} μN</span>
              {' '}drag
            </div>
            <div style={{ ...mono, fontSize: 10, color: C.muted, marginTop: 2 }}>
              {cyclesPerDay.toFixed(0)} pulses / day  ·  J = {(J_pulse * 1e9).toFixed(2)} nNs
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ ...mono, fontSize: 16, color: '#0e9cc0', fontWeight: 700 }}>
              {(propCycle * 1e6).toFixed(8)}
            </span>
            <span style={{ ...mono, fontSize: 10, color: C.muted, marginLeft: 4 }}>mg / pulse</span>
            <div style={{ ...mono, fontSize: 9, color: C.muted }}>
              Δm = M × (1 − e^(−J/(Isp·g₀)))
            </div>
          </div>
        </div>

        {/* Totals row */}
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', paddingTop: 4 }}>
          {[
            { label: "MASS RESET / CYCLE",  val: (propMassReset  * 1e6).toFixed(6), unit: "mg", color: "#a855f7" },
            { label: "HULL PULSE / CYCLE",  val: (propCycle      * 1e6).toFixed(8), unit: "mg", color: "#0e9cc0" },
            { label: "TOTAL / CYCLE",       val: (propTotalCycle * 1e6).toFixed(6), unit: "mg", color: "#f59e0b" },
            { label: "TOTAL / DAY",         val: (propTotalCycle * 86400 / realCycleTime * 1e6).toFixed(4), unit: "mg", color: "#22c55e" },
          ].map(({ label, val, unit, color }) => (
            <div key={label}>
              <div style={{ ...mono, fontSize: 7, color: C.muted, letterSpacing: 1.5, marginBottom: 3 }}>{label}</div>
              <span style={{ ...mono, fontSize: 15, color, fontWeight: 700 }}>{val}</span>
              <span style={{ ...mono, fontSize: 9, color: C.muted, marginLeft: 4 }}>{unit}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Section: Compare ── */}
      <div className="cy-sec-header">
        <div className="cy-sec-dot" style={{ background: '#22c55e' }} />
        <span className="cy-sec-label" style={{ color: '#22c55e' }}>COMPARE WITH OTHER SYSTEMS</span>
        <div className="cy-sec-line" style={{ background: 'linear-gradient(90deg, #22c55e40, transparent)' }} />
      </div>
      {/* ── Comparison toggles ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ ...mono, fontSize: 8, color: C.muted, letterSpacing: 2 }}>COMPARE WITH:</span>
        {[
          { id: "coldgas", label: "STANDALONE COLD GAS", color: "#22c55e",
            sub: "same thruster, no EMMS — whole body direct thrust" },
          { id: "ion",     label: "ION THRUSTER",         color: "#f59e0b",
            sub: `Isp 1500s Hall thruster, same propellant budget` },
        ].map(({ id, label, color, sub }) => {
          const active = compareMode === id;
          return (
            <button key={id} onClick={() => setCompareMode(active ? null : id)}
              style={{
                ...mono, fontSize: 9, padding: "7px 14px", borderRadius: 6, cursor: "pointer",
                background: active ? `${color}20` : "transparent",
                border: `1px solid ${active ? color + "88" : C.border}`,
                color: active ? color : C.muted,
                letterSpacing: 1.2, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2,
              }}>
              <span>{active ? "▼ " : "▶ "}{label}</span>
              <span style={{ fontSize: 7, color: active ? color + "aa" : C.muted, letterSpacing: 0.5 }}>{sub}</span>
            </button>
          );
        })}
        {compareMode && (
          <span style={{ ...mono, fontSize: 8, color: C.muted }}>
            — same scale, same propellant budget, {tele?.cycles ?? 0} cycle{(tele?.cycles ?? 0) !== 1 ? "s" : ""} elapsed
          </span>
        )}
      </div>

      {/* ── Comparison canvas (shown when a mode is active) ── */}
      {/* ── Comparison canvas (shown when a mode is active) ── */}
      {compareMode && (
        <div style={{
          background: C.panel,
          border: `2px solid ${compareMode === 'coldgas' ? '#22c55e' : '#f59e0b'}33`,
          borderTop: `2px solid ${compareMode === 'coldgas' ? '#22c55e' : '#f59e0b'}88`,
          borderRadius: 10, overflow: "hidden", marginBottom: 12,
        }}>
          <CompareCanvas mode={compareMode} params={p} simRef={sim} running={running} />
        </div>
      )}


      {/* ── Section: Phase ── */}
      <div className="cy-sec-header">
        <div className="cy-sec-dot" style={{ background: '#a855f7' }} />
        <span className="cy-sec-label" style={{ color: '#a855f7' }}>CURRENT PHASE</span>
        <div className="cy-sec-line" style={{ background: 'linear-gradient(90deg, #a855f740, transparent)' }} />
      </div>
      {/* ── Phase description ── */}
      <div style={{
        background: `${ph?.color ?? "#0e9cc0"}10`,
        border: `1px solid ${ph?.color ?? "#0e9cc0"}33`,
        borderLeft: `3px solid ${ph?.color ?? "#0e9cc0"}`,
        borderRadius: "0 6px 6px 0", padding: "8px 14px", marginBottom: 12,
      }}>
        <span style={{ ...mono, fontSize: 9, color: ph?.color, fontWeight: 700 }}>
          S{tele?.phase ?? 0} — {ph?.name}
        </span>
        <span style={{ ...mono, fontSize: 9, color: C.muted, marginLeft: 10 }}>
          {ph?.desc}
        </span>
      </div>

      {/* ── Section: Telemetry ── */}
      <div className="cy-sec-header">
        <div className="cy-sec-dot" style={{ background: '#0e9cc0' }} />
        <span className="cy-sec-label" style={{ color: '#0e9cc0' }}>LIVE TELEMETRY</span>
        <div className="cy-sec-line" style={{ background: 'linear-gradient(90deg, #0e9cc040, transparent)' }} />
      </div>
      {/* ── Telemetry grid ── */}
      <div className="cy-tele-grid">
        <div style={{ background: C.panel, border: `1px solid #0e9cc033`, borderTop: `2px solid #0e9cc066`, borderRadius: 8, padding: 14 }}>
          <div style={{ ...mono, fontSize: 8, color: "#0e9cc0", letterSpacing: 2, marginBottom: 10 }}>HULL KINEMATICS</div>
          {[
            ["velocity",     (tele?.vHull ?? 0) * 1000, "mm/s",  6],
            ["acceleration", (tele?.aHull ?? 0) * 1000, "mm/s²", 4],
            ["orbit error",  (tele?.xHull ?? 0) * 1000, "mm",    3],
            ["F_thrust",     (tele?.ft    ?? 0) * 1000, "mN",    2],
          ].map(([l, v, u, d]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: `1px solid ${C.border}20` }}>
              <span style={{ ...mono, fontSize: 8, color: C.muted }}>{l}</span>
              <span style={{ ...mono, fontSize: 11, color: "#0e9cc0", fontWeight: 600 }}>
                {v.toFixed(d)} <span style={{ fontSize: 8, color: C.muted }}>{u}</span>
              </span>
            </div>
          ))}
        </div>

        <div style={{ background: C.panel, border: `1px solid #22c55e33`, borderTop: `2px solid #22c55e66`, borderRadius: 8, padding: 14 }}>
          <div style={{ ...mono, fontSize: 8, color: "#22c55e", letterSpacing: 2, marginBottom: 10 }}>EMMS MASS KINEMATICS</div>
          {[
            ["velocity",     (tele?.vMass  ?? 0) * 1000, "mm/s",  6],
            ["acceleration", (tele?.aMass  ?? 0) * 1000, "mm/s²", 2],
            ["displacement", (tele?.xRel   ?? 0) * 1000, "mm",    3],
            ["F_emms",       (tele?.fe     ?? 0) * 1000, "mN",    1],
          ].map(([l, v, u, d]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: `1px solid ${C.border}20` }}>
              <span style={{ ...mono, fontSize: 8, color: C.muted }}>{l}</span>
              <span style={{ ...mono, fontSize: 11, color: "#22c55e", fontWeight: 600 }}>
                {v.toFixed(d)} <span style={{ fontSize: 8, color: C.muted }}>{u}</span>
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Section: Metrics ── */}
      <div className="cy-sec-header">
        <div className="cy-sec-dot" style={{ background: '#f59e0b' }} />
        <span className="cy-sec-label" style={{ color: '#f59e0b' }}>CYCLE METRICS</span>
        <div className="cy-sec-line" style={{ background: 'linear-gradient(90deg, #f59e0b40, transparent)' }} />
      </div>
      {/* ── Results ── */}
      <div className="cy-results-grid">
        {[
          ["Cycle time (real)", realCycleTime.toFixed(1),  "s",       C.text],
          ["Cycles / day",     (86400 / realCycleTime).toFixed(1), "", C.text],
          ["Prop / cycle",     propPerCycleG.toFixed(5), "g",       "#f59e0b"],
          ["Prop / year",      propPerYearG.toFixed(2),  "g/yr",    "#ef4444"],
          ["EMMS max travel",  maxDispMM.toFixed(2),     "mm",      "#22c55e"],
          ["EMMS range (±)",   (p.L_emms / 2 * 1000).toFixed(0), "mm", C.muted],
          ["Net Δ-pos",        netDisplaceMM.toFixed(3), "mm",      "#0e9cc0"],
          ["Cycles run",       (tele?.cycles ?? 0).toString(), "",  "#a855f7"],
        ].map(([l, v, u, col]) => (
          <div key={l} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 6, padding: "10px 12px" }}>
            <div style={{ ...mono, fontSize: 7, color: C.muted, letterSpacing: 1.5, marginBottom: 4 }}>{l}</div>
            <div style={{ ...mono, fontSize: 16, fontWeight: 700, color: col }}>
              {v}<span style={{ fontSize: 9, color: C.muted, marginLeft: 3 }}>{u}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ── Section: Stroke Timing ── */}
      <div className="cy-sec-header">
        <div className="cy-sec-dot" style={{ background: '#a855f7' }} />
        <span className="cy-sec-label" style={{ color: '#a855f7' }}>STROKE TIMING BREAKDOWN</span>
        <div className="cy-sec-line" style={{ background: 'linear-gradient(90deg, #a855f740, transparent)' }} />
      </div>
      {/* ── Stroke timing card (full width) ── */}
      <div style={{
        background: C.eqBg, border: `1px solid ${C.eqBorder}`,
        borderTop: `2px solid ${C.border}`, borderRadius: 6, padding: "14px 16px", marginBottom: 8,
      }}>
        <div style={{ ...mono, fontSize: 10, color: C.text, letterSpacing: 3, marginBottom: 10, fontWeight: 700 }}>
          REAL PHYSICS DURATION PER STROKE  —  one complete S0→S5 cycle
        </div>
        <div className="cy-stroke-grid">
          {[
            {
              phase: "S0", name: "STABLE HOLD", t: t_s0, col: PH[0].color,
              formula: `J_hull / F_drag`,
              values:  `${(p.F_thrust*p.dt_thrust*1e6).toFixed(2)}μNs / ${(p.F_drag*1e6).toFixed(0)}μN`,
            },
            {
              phase: "S1", name: "REPULSION", t: t_s1, col: PH[1].color,
              formula: `8 × τ_coil`,
              values:  `8 × ${(p.tau_coil*1000).toFixed(0)}ms`,
            },
            {
              phase: "S2", name: "COAST→DAM", t: t_s2, col: PH[2].color,
              formula: `L_emms / v_rel`,
              values:  `${(p.L_emms*1000).toFixed(0)}mm / ${(vRelAfterS1*1000).toFixed(1)}mm/s`,
            },
            {
              phase: "S3", name: "DAMPING", t: t_s3, col: PH[3].color,
              formula: `fixed`,
              values:  `inelastic stop`,
            },
            {
              phase: "S4", name: "COLD GAS MIB", t: t_s4, col: PH[4].color,
              formula: `J_mib / F_reset`,
              values:  `${(p.J_mib*1000).toFixed(1)}mNs / ${(p.F_reset*1000).toFixed(0)}mN`,
            },
            {
              phase: "S5", name: "COAST→REP", t: p.t_coast_wall, col: PH[5].color,
              formula: `mission coast window`,
              values:  `physics: ${t_s5.toFixed(1)}s = ${(p.L_emms*1000).toFixed(0)}mm÷${(p.J_mib/p.m_emms*1000).toFixed(1)}mm/s`,
            },
          ].map(({ phase, name, t, col, formula, values }) => (
            <div key={phase} style={{
              borderLeft: `3px solid ${col}66`,
              background: col + '0a', borderRadius: "0 5px 5px 0", padding: "10px 10px",
            }}>
              <div style={{ ...mono, fontSize: 7, color: col, letterSpacing: 1, marginBottom: 5 }}>
                {phase} · {name}
              </div>
              <div style={{ ...mono, fontSize: 18, fontWeight: 700, color: col, lineHeight: 1 }}>
                {t >= 1 ? t.toFixed(t >= 10 ? 1 : 2) : (t * 1000).toFixed(0)}
                <span style={{ fontSize: 9, color: C.muted, marginLeft: 3 }}>
                  {t >= 1 ? "s" : "ms"}
                </span>
              </div>
              <div style={{ ...mono, fontSize: 7, color: col + 'bb', marginTop: 5 }}>
                {formula}
              </div>
              <div style={{ ...mono, fontSize: 7, color: C.muted, marginTop: 2 }}>
                {values}
              </div>
              <div style={{ marginTop: 6, height: 3, background: C.border + '40', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(t / realCycleTime) * 100}%`,
                  background: col + 'aa', borderRadius: 2 }} />
              </div>
              <div style={{ ...mono, fontSize: 6, color: col + '99', marginTop: 2 }}>
                {((t / realCycleTime) * 100).toFixed(1)}%
              </div>
            </div>
          ))}
        </div>

        {/* Physics check note */}
        <div style={{ marginTop: 10, padding: "8px 10px", background: C.eqBg, borderRadius: 4, border: `1px solid ${C.eqBorder}` }}>
          <div style={{ ...mono, fontSize: 7, color: C.muted, letterSpacing: 1.5, marginBottom: 4 }}>PHYSICS CHECK</div>
          <div style={{ ...mono, fontSize: 8, color: C.text, lineHeight: 1.8 }}>
            {`S5 dominates at ${((t_s5/realCycleTime)*100).toFixed(1)}% — because v_mass = J_mib/m_emms = ${(p.J_mib*1000).toFixed(1)}mNs/${p.m_emms}kg = ${(p.J_mib/p.m_emms*1000).toFixed(1)}mm/s  →  t = ${(p.L_emms*1000).toFixed(0)}mm ÷ ${(p.J_mib/p.m_emms*1000).toFixed(1)}mm/s = ${t_s5.toFixed(1)}s`}
          </div>
          <div style={{ ...mono, fontSize: 8, color: C.muted, lineHeight: 1.8 }}>
            {`To reduce S5: increase J_mib (larger MIB) or reduce L_emms (shorter EMMS stroke).`}
            {`  Current: ${(86400/realCycleTime).toFixed(1)} cycles/day  ·  ${realCycleTime.toFixed(2)}s/cycle`}
          </div>
        </div>

        {/* Cycle total */}
        <div style={{ marginTop: 8, display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap" }}>
          <span style={{ ...mono, fontSize: 7, color: C.muted, letterSpacing: 1.5 }}>TOTAL / CYCLE</span>
          <span style={{ ...mono, fontSize: 22, fontWeight: 700, color: C.text }}>
            {realCycleTime.toFixed(2)}
            <span style={{ fontSize: 10, color: C.muted, marginLeft: 5 }}>s</span>
          </span>
          <span style={{ ...mono, fontSize: 8, color: C.muted }}>
            = {t_s0.toFixed(3)}s + {t_s1.toFixed(3)}s + {t_s2.toFixed(2)}s + {t_s3.toFixed(2)}s + {t_s4.toFixed(3)}s + {(p.t_coast_wall*1000).toFixed(0)}ms
          </span>
        </div>
      </div>

      {/* ── Section: Mission Stats ── */}
      <div className="cy-sec-header">
        <div className="cy-sec-dot" style={{ background: '#f59e0b' }} />
        <span className="cy-sec-label" style={{ color: '#f59e0b' }}>MISSION ACCUMULATION</span>
        <div className="cy-sec-line" style={{ background: 'linear-gradient(90deg, #f59e0b40, transparent)' }} />
      </div>
      {/* ── Total mission stats ── */}
      <div className="cy-mission-grid">
        {/* Total propellant spent */}
        <div style={{
          background: C.panel, border: `1px solid #f59e0b44`,
          borderTop: `2px solid #f59e0b88`, borderRadius: 6, padding: "12px 16px",
        }}>
          <div style={{ ...mono, fontSize: 7, color: '#f59e0b', letterSpacing: 2, marginBottom: 6 }}>TOTAL PROPELLANT SPENT</div>
          <div style={{ ...mono, fontSize: 22, fontWeight: 700, color: '#f59e0b' }}>
            {(propPerCycleG * (tele?.cycles ?? 0)).toFixed(4)}
            <span style={{ fontSize: 9, color: C.muted, marginLeft: 5 }}>g</span>
          </div>
          <div style={{ ...mono, fontSize: 8, color: C.muted, marginTop: 4 }}>
            {propPerCycleG.toFixed(5)} g/cycle × {tele?.cycles ?? 0} cycles
          </div>
        </div>

        {/* Total mission time (real physics, not animation time) */}
        <div style={{
          background: C.panel, border: `1px solid #a855f744`,
          borderTop: `2px solid #a855f788`, borderRadius: 6, padding: "12px 16px",
        }}>
          <div style={{ ...mono, fontSize: 7, color: '#a855f7', letterSpacing: 2, marginBottom: 6 }}>TOTAL MISSION TIME</div>
          <div style={{ ...mono, fontSize: 22, fontWeight: 700, color: '#a855f7' }}>
            {totalMissionTime >= 60
              ? `${Math.floor(totalMissionTime / 60)}m ${(totalMissionTime % 60).toFixed(0)}s`
              : totalMissionTime.toFixed(2) + ' s'}
            <span style={{ fontSize: 9, color: C.muted, marginLeft: 5 }}>
              {totalMissionTime >= 60 ? '' : ''}
            </span>
          </div>
          <div style={{ ...mono, fontSize: 8, color: C.muted, marginTop: 4 }}>
            {(tele?.cycles ?? 0)} cycles × {realCycleTime.toFixed(2)} s/cycle
          </div>
        </div>
      </div>

      {/* ── Section: Equations ── */}
      <div className="cy-sec-header">
        <div className="cy-sec-dot" style={{ background: '#0e9cc0' }} />
        <span className="cy-sec-label" style={{ color: '#0e9cc0' }}>PHYSICS EQUATIONS</span>
        <div className="cy-sec-line" style={{ background: 'linear-gradient(90deg, #0e9cc040, transparent)' }} />
      </div>
      {/* ── Equations ── */}
      <div style={{ background: C.eqBg, border: `1px solid ${C.eqBorder}`, borderRadius: 6, padding: "14px 18px" }}>
        <div style={{ ...mono, fontSize: 8, color: C.muted, letterSpacing: 2, marginBottom: 8 }}>LIVE EQUATIONS</div>
        {[
          `M_total = m_hull + m_emms = ${p.m_hull} + ${p.m_emms} = ${(p.m_hull + p.m_emms).toFixed(1)} kg`,
          `J_pulse  = F_thrust × dt_thrust = ${(p.F_thrust * 1000).toFixed(1)}mN × ${(p.dt_thrust * 1000).toFixed(2)}ms = ${(J_pulse * 1e6).toFixed(2)} μNs`,
          `t_S0     = J_hull_pulse / F_drag = ${(J_pulse * 1e6).toFixed(2)}μNs / ${(p.F_drag * 1e6).toFixed(0)}μN = ${tCycle.toFixed(3)} s  (stable hold — drag recovery window)`,
          `t_cycle  = S0+S1+S2+S3+S4+S5 = ${tCycle.toFixed(3)}+${t_s1.toFixed(3)}+${t_s2.toFixed(2)}+${t_s3.toFixed(2)}+${t_s4.toFixed(3)}+${(p.t_coast_wall).toFixed(2)} = ${realCycleTime.toFixed(2)} s  (S5 coast = mission input; physics = ${t_s5.toFixed(1)}s)`,
          `// S1 REPULSION STROKE: EMMS fires; fe<0 → mass→damper, hull→repulsion`,
          `a_hull(S1) = +F_absorb/m_hull = +${(p.F_absorb*1000).toFixed(0)}mN/${p.m_hull}kg = +${(p.F_absorb/p.m_hull*1000).toFixed(1)} mm/s²`,
          `a_mass(S1) = −F_absorb/m_emms = −${(p.F_absorb*1000).toFixed(0)}mN/${p.m_emms}kg = −${(p.F_absorb/p.m_emms*1000).toFixed(0)} mm/s²  (toward damper)`,
          `// S2 COASTING STROKE: EMMS off; mass coasts left, hull coasts right — opposite directions`,
          `v_mass(S2) ≈ −${(p.F_absorb/p.m_emms*p.tau_coil*8*1000).toFixed(0)} mm/s  |  v_hull ≈ +${(p.F_absorb/p.m_hull*p.tau_coil*8*1000).toFixed(0)} mm/s`,
          `// S4 COLD GAS (MIB): external force on mass only; hull physically frozen (a_hull=0) ✓`,
          `dt_burn  = J_mib / F_reset = ${(p.J_mib*1000).toFixed(1)}mNs / ${(p.F_reset*1000).toFixed(0)}mN = ${(p.J_mib/p.F_reset*1000).toFixed(0)} ms  (brief pulse)`,
          `v_mib    = J_mib / m_emms = ${(p.J_mib*1000).toFixed(1)}mNs / ${p.m_emms}kg = ${(p.J_mib/p.m_emms*1000).toFixed(1)} mm/s  (mass velocity after burn)`,
          `// S5 MASS COASTING: mass coasts at v_mib; hull frozen; CoM drifts at v_mib·(m_emms/M)`,
          `t_coast  = L_emms / v_mib = ${(p.L_emms*1000).toFixed(0)}mm / ${(p.J_mib/p.m_emms*1000).toFixed(1)}mm/s = ${(p.L_emms*p.m_emms/p.J_mib).toFixed(1)} s`,
          `v_com(S5) = J_mib / M_total = ${(p.J_mib*1000).toFixed(1)}mNs / ${(p.m_hull+p.m_emms).toFixed(1)}kg = ${(p.J_mib/(p.m_hull+p.m_emms)*1000).toFixed(3)} mm/s  (CoM drift)`,
          `Δx_cycle = v_com × t_coast = (m_emms/M)·L_emms = ${((p.m_emms/(p.m_hull+p.m_emms))*p.L_emms*1000).toFixed(2)} mm`,
          `prop/cycle = M_total × (1 − exp(−J / (Isp × g₀))) = ${propPerCycleG.toFixed(5)} g`,
        ].map((line, i) => (
          <div key={i} style={{
            ...mono, fontSize: 10, color: line.startsWith('//') ? C.muted : C.text, lineHeight: 2,
          }}>{line}</div>
        ))}
      </div>

      {/* ── Attitude Control Comparison ── */}
      <div className="cy-sec-header" style={{ marginTop: 32 }}>
        <div className="cy-sec-dot" style={{ background: '#6366f1' }} />
        <span className="cy-sec-label" style={{ color: '#6366f1' }}>ATTITUDE CONTROL — COLD GAS MIB vs REACTION WHEEL</span>
        <div className="cy-sec-line" style={{ background: 'linear-gradient(90deg, #6366f140, transparent)' }} />
      </div>
      <div style={{ marginBottom: 20 }}>
        <AttitudeCanvas params={p} />
      </div>
      {/* ── 3D Orbital Coverage ── */}
      <div className="cy-sec-header" style={{ marginTop: 32 }}>
        <div className="cy-sec-dot" style={{ background: '#0e9cc0' }} />
        <span className="cy-sec-label" style={{ color: '#0e9cc0' }}>ORBITAL COVERAGE — EP-ADCS SIDEWAYS DISPLACEMENT</span>
        <div className="cy-sec-line" style={{ background: 'linear-gradient(90deg, #0e9cc040, transparent)' }} />
      </div>
      <div style={{ ...mono, fontSize: 11, color: C.muted, marginBottom: 12, lineHeight: 1.8 }}>
        EP-ADCS shifts{' '}
        <span style={{ color: '#22c55e' }}>{((p.m_emms / (p.m_hull + p.m_emms)) * p.L_emms * 1000).toFixed(2)} mm</span>
        {' '}per cycle.  Full cycle (S0→S5 including{' '}
        <span style={{ color: '#a855f7' }}>{p.t_coast_wall.toFixed(2)} s coast</span>
        {') = '}
        <span style={{ color: '#f59e0b' }}>{realCycleTime.toFixed(2)} s</span>
        {' '}→{' '}
        <span style={{ color: '#0e9cc0', fontWeight: 700 }}>
          {(60 / realCycleTime).toFixed(1)} cycles/min × {(T_ORB_CYCLE / 60).toFixed(1)} min orbit
          {' '}= {(T_ORB_CYCLE / realCycleTime).toFixed(0)} cycles/orbit
          {' '}→ {((p.m_emms / (p.m_hull + p.m_emms)) * p.L_emms * T_ORB_CYCLE / realCycleTime).toFixed(1)} m shift / orbit.
        </span>
      </div>
      <OrbitalCoverageCanvas
        cycleStrokeM={(p.m_emms / (p.m_hull + p.m_emms)) * p.L_emms}
        tCycleS={realCycleTime}
      />

      {/* ── Standalone CG Canvas Comparison ── */}
      <div className="cy-sec-header" style={{ marginTop: 32 }}>
        <div className="cy-sec-dot" style={{ background: '#22c55e' }} />
        <span className="cy-sec-label" style={{ color: '#22c55e' }}>ORBITAL MANEUVER — STANDALONE COLD GAS COMPARISON</span>
        <div className="cy-sec-line" style={{ background: 'linear-gradient(90deg, #22c55e40, transparent)' }} />
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ ...mono, fontSize: 9, color: C.muted, letterSpacing: 1 }}>TARGET DISPLACEMENT:</span>
        <input type="number" min="1" max="50000" step="10"
          value={orbitShiftMM}
          onChange={e => setOrbitShiftMM(Math.max(1, parseFloat(e.target.value) || 100))}
          style={{
            width: 88, padding: '4px 8px', ...mono, fontSize: 13,
            background: C.eqBg, border: `1px solid #22c55e66`,
            color: '#22c55e', borderRadius: 4, textAlign: 'right',
          }} />
        <span style={{ ...mono, fontSize: 10, color: '#22c55e' }}>mm</span>
        <span style={{ ...mono, fontSize: 9, color: C.muted }}>— how far should the satellite reposition?</span>
      </div>
      <StandaloneCGCanvas mass={p.m_hull + p.m_emms} targetMM={orbitShiftMM} />

    </div>
  );
}
